/**
 * TEMP-H-005: Shared Types Library
 * 
 * Bridge between backend contracts and frontend types.
 * Re-exports backend schemas with runtime validation and type guards.
 * Replaces local type definitions to ensure consistency.
 */

import { z } from 'zod';

// =============================================================================
// STYLE TAXONOMY & IMAGE STYLES
// =============================================================================

/**
 * TEMP-018: Image style enum - matches backend ImageStyleEnum
 */
export const ImageStyleEnum = z.enum([
  'photorealistic',
  'animated', 
  'anime',
  'comic_book',
  '3d_animation',
  'illustration',
  'sketch',
  'line_art',
  'low_poly',
  'pixel_art',
  'psychedelic'
]);

export type ImageStyle = z.infer<typeof ImageStyleEnum>;

// =============================================================================
// STAGE TYPES & ENUMS
// =============================================================================

/**
 * Canonical stage names enum - matches backend exactly
 */
export const Stage = z.enum([
  'script', 
  'vision', 
  'storyboard', 
  'shots', 
  'continuity', 
  'elements', 
  'frames', 
  'videos',
  'qc',
  'assemble',
  'music',
  'music_master',
  'music_sidechain', 
  'export_preview',
  'publish'
]);

export type StageName = z.infer<typeof Stage>;

/**
 * Legacy type for backward compatibility
 * @deprecated Use StageName instead
 */
export type StageType = StageName;

/**
 * Execution status types
 */
export const ExecutionStatus = z.enum([
  'queued',
  'running', 
  'paused',
  'completed',
  'failed',
  'cancelled'
]);

export type ExecutionStatusType = z.infer<typeof ExecutionStatus>;

// =============================================================================
// STAGE SETTINGS CONTRACTS
// =============================================================================

/**
 * Individual stage settings schema - matches backend
 */
export const StageSettingsSchema = z.object({
  stage: Stage,
  qcPersonaSlug: z.string(),
  passMin: z.number().min(0).max(1),
  fallbackMin: z.number().min(0).max(1),
  retryLimit: z.number().int().min(0).max(5),
  categoryThresholds: z.record(z.string(), z.number().min(0).max(1)).default({}),
  categoryWeights: z.record(z.string(), z.number().min(0).max(1)).default({}),
  isDefaultFromLaunch: z.boolean(),
  updatedAt: z.string().datetime()
});

export type StageSettings = z.infer<typeof StageSettingsSchema>;

/**
 * Complete execution settings response
 */
export const ExecutionSettingsSchema = z.object({
  executionId: z.string().uuid(),
  version: z.string(),
  stages: z.array(StageSettingsSchema)
});

export type ExecutionSettings = z.infer<typeof ExecutionSettingsSchema>;

/**
 * Stage thresholds response schema
 */
export const StageThresholdsSchema = z.object({
  executionId: z.string().uuid(),
  stage: Stage,
  version: z.string(),
  qcPersonaSlug: z.string(),
  passMin: z.number().min(0).max(1),
  fallbackMin: z.number().min(0).max(1),
  retryLimit: z.number().int().min(0).max(5),
  categoryThresholds: z.record(z.string(), z.number().min(0).max(1)).default({}),
  categoryWeights: z.record(z.string(), z.number().min(0).max(1)).default({}),
  updatedAt: z.string().datetime()
});

export type StageThresholds = z.infer<typeof StageThresholdsSchema>;

/**
 * PATCH request schema for updating stage settings
 */
export const PatchStageSettingsInputSchema = z.object({
  passMin: z.number().min(0).max(1).optional(),
  fallbackMin: z.number().min(0).max(1).optional(),
  retryLimit: z.number().int().min(0).max(5).optional(),
  categoryThresholds: z.record(z.string(), z.number().min(0).max(1)).optional(),
  categoryWeights: z.record(z.string(), z.number().min(0).max(1)).optional()
}).refine((v: any) => {
  if (v.passMin !== undefined && v.fallbackMin !== undefined) {
    return v.fallbackMin <= v.passMin;
  }
  return true;
}, { 
  message: 'fallbackMin must be <= passMin' 
}).refine((v: any) => {
  if (v.categoryWeights) {
    const weights = Object.values(v.categoryWeights) as number[];
    if (weights.length === 0) return true;
    const sum = weights.reduce((acc: number, w: number) => acc + w, 0);
    return Math.abs(sum - 1.0) < 0.001;
  }
  return true;
}, {
  message: 'categoryWeights must sum to 1.0'
});

export type PatchStageSettingsInput = z.infer<typeof PatchStageSettingsInputSchema>;

/**
 * Standard API update response
 */
export const UpdateResponseSchema = z.object({
  ok: z.boolean(),
  updatedAt: z.string().datetime(),
  message: z.string().optional()
});

export type UpdateResponse = z.infer<typeof UpdateResponseSchema>;

// =============================================================================
// EVENT TYPES & CONTRACTS  
// =============================================================================

/**
 * Base event schema - all events extend this
 */
export const BaseEventSchema = z.object({
  execution_id: z.string().uuid(),
  stage: Stage,
  timestamp: z.string().datetime(),
  correlation_id: z.string().optional(),
  workflow_id: z.string().optional(),
  workflow_run_id: z.string().optional(),
  workflow_namespace: z.string().optional()
});

export type BaseEvent = z.infer<typeof BaseEventSchema>;

/**
 * Content ready event schema
 */
export const ContentReadyEventSchema = BaseEventSchema.extend({
  event_type: z.literal('content_ready'),
  payload: z.object({
    files: z.array(z.string()),
    refs: z.record(z.string(), z.string()),
    durationMs: z.number().optional(),
    costUsd: z.number().optional()
  })
});

export type ContentReadyEvent = z.infer<typeof ContentReadyEventSchema>;

/**
 * Scores ready event schema
 */
export const ScoresReadyEventSchema = BaseEventSchema.extend({
  event_type: z.literal('scores_ready'),
  payload: z.object({
    target_stage: Stage,
    persona_slug: z.string(),
    rubricHash: z.string(),
    weighted_score: z.number().min(0).max(1),
    category_scores: z.record(z.string(), z.number()),
    passed: z.boolean(),
    borderline: z.boolean().optional(),
    hard_fail_categories: z.array(z.string()).optional()
  })
});

export type ScoresReadyEvent = z.infer<typeof ScoresReadyEventSchema>;

/**
 * Stage started event schema  
 */
export const StageStartedEventSchema = BaseEventSchema.extend({
  event_type: z.literal('stage_started'),
  payload: z.object({
    run_id: z.string(),
    config: z.record(z.string(), z.any()).optional()
  })
});

export type StageStartedEvent = z.infer<typeof StageStartedEventSchema>;

/**
 * Stage completed event schema - matches backend Events v2
 */
export const StageCompletedEventSchema = BaseEventSchema.extend({
  event_type: z.literal('stage_completed'),
  payload: z.object({
    run_id: z.string().describe('Activity run ID'),
    success: z.boolean().describe('Whether stage succeeded'),
    files: z.array(z.string()).optional().describe('Output files'),
    metrics: z.record(z.string(), z.any()).optional().describe('Stage metrics'),
    quality_score: z.number().min(0).max(1).optional().describe('Quality score if evaluated')
  })
});

export type StageCompletedEvent = z.infer<typeof StageCompletedEventSchema>;

/**
 * Stage failed event schema - matches backend Events v2
 */
export const StageFailedEventSchema = BaseEventSchema.extend({
  event_type: z.literal('stage_failed'),
  payload: z.object({
    run_id: z.string().describe('Activity run ID'),
    error: z.string().describe('Error message'),
    error_type: z.string().optional().describe('Error classification'),
    retryable: z.boolean().optional().describe('Whether error is retryable'),
    attempt: z.number().optional().describe('Attempt number')
  })
});

export type StageFailedEvent = z.infer<typeof StageFailedEventSchema>;

/**
 * Billing committed event schema - TEMP-D-001
 */
export const BillingEventSchema = BaseEventSchema.extend({
  event_type: z.literal('billing_committed'),
  payload: z.object({
    run_id: z.string().describe('Activity run ID'),
    costUsd: z.number().describe('Cost to bill in USD'),
    billableDelta: z.number().describe('Cost increment for this commit in USD'),
    pipelineTotalUsd: z.number().describe('Running pipeline total after commit in USD'),
    provider: z.string().optional().describe('AI provider'),
    model: z.string().optional().describe('Model used'),
    tokensUsed: z.number().optional().describe('Tokens consumed'),
    committed_at: z.string().datetime().describe('Billing commit timestamp')
  })
});

export type BillingEvent = z.infer<typeof BillingEventSchema>;

/**
 * Union of all pipeline event types - matches backend Events v2 exactly
 */
export const PipelineEventSchema = z.discriminatedUnion('event_type', [
  ContentReadyEventSchema,
  ScoresReadyEventSchema, 
  StageStartedEventSchema,
  StageCompletedEventSchema,
  StageFailedEventSchema,
  BillingEventSchema
]);

export type PipelineEvent = z.infer<typeof PipelineEventSchema>;

/**
 * Event type enum for filtering - matches backend Events v2
 */
export const EventType = z.enum([
  'content_ready',
  'scores_ready', 
  'stage_started',
  'stage_completed',
  'stage_failed',
  'billing_committed'
]);

export type EventTypeName = z.infer<typeof EventType>;

// =============================================================================
// QC SUMMARY CONTRACTS
// =============================================================================

/**
 * QC decision enum
 */
export const QCDecision = z.enum(['pass', 'fail', 'retry', 'escalate']);

export type QCDecisionType = z.infer<typeof QCDecision>;

/**
 * QC summary response schema
 */
export const QCSummarySchema = z.object({
  executionId: z.string().uuid(),
  stage: Stage,
  iteration: z.number().int().min(1),
  personaSlug: z.string(),
  weightedScore: z.number().min(0).max(1),
  categoryScores: z.record(z.string(), z.number()),
  passed: z.boolean(),
  borderline: z.boolean().optional(),
  hardFailCategories: z.array(z.string()).optional(),
  decision: QCDecision,
  reportRef: z.string().url().optional(),
  updatedAt: z.string().datetime()
});

export type QCSummary = z.infer<typeof QCSummarySchema>;

// =============================================================================
// ERROR HANDLING
// =============================================================================

/**
 * Standard API error response schema
 */
export const ErrorResponseSchema = z.object({
  message: z.string(),
  details: z.any().optional(),
  code: z.string().optional(),
  timestamp: z.string().datetime().optional()
});

export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;

// =============================================================================
// VALIDATION UTILITIES
// =============================================================================

/**
 * Validates API response data against schema, throws on failure
 */
export function validateApiResponse<T>(
  schema: z.ZodSchema<T>,
  data: unknown,
  context: string
): T {
  try {
    return schema.parse(data);
  } catch (error) {
    console.error(`[${context}] API response validation failed:`, error);
    throw new Error(`Invalid API response format for ${context}`);
  }
}

/**
 * Safe validation that returns result with error info
 */
export function safeValidateApiResponse<T>(
  schema: z.ZodSchema<T>, 
  data: unknown,
  context: string
): { success: true; data: T } | { success: false; error: string } {
  const result = schema.safeParse(data);
  if (result.success) {
    return { success: true, data: result.data };
  } else {
    console.error(`[${context}] API response validation failed:`, result.error);
    return { 
      success: false, 
      error: `Invalid API response format for ${context}: ${result.error.message}` 
    };
  }
}

// =============================================================================
// EXECUTION CONFIGURATION SCHEMAS (TEMP-018)
// =============================================================================

/**
 * TEMP-H-007: Music configuration for launch modal
 */
export const MusicConfigSchema = z.object({
  enabled: z.boolean().default(true),
  brief: z.string().optional(),
  personaSlug: z.string().optional()
});

export type MusicConfig = z.infer<typeof MusicConfigSchema>;

/**
 * TEMP-H-007: Advanced export settings for launch modal
 */
export const ExportAdvancedSettingsSchema = z.object({
  resolution: z.enum(['720p', '1080p', '4K']).default('1080p'),
  frame_rate: z.enum(['24', '30', '60']).default('24'),
  bitrate: z.number().min(1).max(50).default(8) // Mbps
});

export type ExportAdvancedSettings = z.infer<typeof ExportAdvancedSettingsSchema>;

/**
 * Stage configuration base schema
 */
export const StageConfigSchema = z.object({
  enabled: z.boolean(),
  target_threshold: z.number().min(0).max(1),
  fallback_threshold: z.number().min(0).max(1),
  max_iterations: z.number().min(1).max(10),
  strategy_switch_after: z.number().min(1).max(5),
  primary_model: z.string(),
  backup_model: z.string().optional(),
  max_cost_per_shot: z.number().min(0),
  timeout_seconds: z.number().min(30),
  custom_instructions: z.string().optional(),
  advanced: z.object({
    diagnostic_mode: z.boolean().optional(),
    force_accept: z.boolean().optional(),
    skip_quality_check: z.boolean().optional()
  }).passthrough().optional()
});

export type StageConfig = z.infer<typeof StageConfigSchema>;

/**
 * Frame-specific stage configuration
 */
export const FrameConfigSchema = StageConfigSchema.extend({
  use_reference_images: z.boolean(),
  enforce_image_style: z.boolean(),
  adaptive_strategy: z.boolean(),
  load_continuity_context: z.boolean(),
  cross_shot_continuity: z.boolean(),
  aspect_ratio: z.enum(['9:16', '16:9', '1:1']),
  quality_preset: z.enum(['photorealistic', 'artistic', 'stylized'])
});

export type FrameConfig = z.infer<typeof FrameConfigSchema>;

/**
 * Videos-specific stage configuration
 */
export const VideosConfigSchema = StageConfigSchema.extend({
  primary_provider: z.enum(['kling', 'runway']),
  backup_provider: z.enum(['runway', 'kling']),
  motion_intensity: z.enum(['subtle', 'medium', 'dynamic']),
  keyframe_mode: z.enum(['start_only', 'start_end', 'keyframe_sequence']),
  resolution: z.enum(['720p', '1080p', '4K']),
  frame_rate: z.enum(['24', '30', '60']),
  complete_creative_context: z.boolean(),
  strategic_prompting: z.boolean(),
  adaptive_motion_strategy: z.boolean()
});

export type VideosConfig = z.infer<typeof VideosConfigSchema>;

/**
 * Complete pipeline configuration schema - TEMP-018
 */
export const PipelineConfigSchema = z.object({
  scene_id: z.string(),
  asset_path: z.string(),
  
  // Brand context
  brand: z.enum(['hisi', 'blackflags']).optional(),
  
  // TEMP-018: New execution-level settings (no legacy visual_style support in FE)
  image_style: ImageStyleEnum.optional(),
  orientation_default: z.enum(['9:16', '16:9', '1:1']).default('9:16'),
  export_quality_preset: z.enum(['fast', 'balanced', 'quality']).default('balanced'),
  
  // TEMP-H-007: Music configuration
  music_config: MusicConfigSchema.optional(),
  
  // TEMP-H-007: Advanced export settings (applied at export time)
  export_advanced_settings: ExportAdvancedSettingsSchema.optional(),
  
  // Stage Configurations
  script: StageConfigSchema,
  vision: StageConfigSchema,
  storyboard: StageConfigSchema,
  shots: StageConfigSchema,
  continuity: StageConfigSchema,
  elements: StageConfigSchema.optional(),
  frames: FrameConfigSchema,
  videos: VideosConfigSchema,
  qc: StageConfigSchema.optional(),
  assemble: StageConfigSchema.optional(),
  music: StageConfigSchema.optional(),
  publish: StageConfigSchema.optional(),
  
  // Global Settings
  budget_limit_usd: z.number().min(0),
  quality_target: z.enum(['production', 'preview', 'draft']),
  parallel_execution: z.boolean(),
  auto_retry: z.boolean(),
  checkpoint_frequency: z.enum(['every_stage', 'on_failure', 'manual']),
  diagnostic_mode: z.boolean().optional()
});

export type PipelineConfig = z.infer<typeof PipelineConfigSchema>;